import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useState,
} from 'react';

import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Input, Select, Hidden, ComboboxMultiple } from '~/components/Form';
import { t } from '~/helpers';
import { validation, validationErrors } from '~/helpers/yupValidations';

import * as S from './styles';

const isFilled = (value) =>
  value !== null && value !== undefined && value !== '';
const isTextFilled = (value) => isFilled(value) && String(value).trim() !== '';
const hasItems = (items = []) => !isEmpty(items);
const EMPTY_ARRAY = [];

const getIdTanque = (tanque) => {
  const value = tanque?.idtanquefisico || tanque?.value || tanque;

  return isFilled(value) ? Number(value) : NaN;
};

const getIdConfigAlarmeTanque = (alarme) => {
  const value = alarme?.idconfigalarmetanque || alarme?.value || alarme;

  return isFilled(value) ? Number(value) : NaN;
};

const normalizaAlarmes = (alarmes = []) =>
  alarmes
    .map((alarme) => ({
      ...(typeof alarme === 'object' ? alarme : {}),
      idconfigalarmetanque: getIdConfigAlarmeTanque(alarme),
    }))
    .filter((alarme) => !Number.isNaN(alarme.idconfigalarmetanque));

const normalizaTanquesEntrada = (tanques = []) =>
  tanques
    .map((tanque) => ({
      ...(typeof tanque === 'object' ? tanque : {}),
      idtanquefisico: getIdTanque(tanque),
      alarmes: normalizaAlarmes(tanque?.alarmes || []),
    }))
    .filter((tanque) => !Number.isNaN(tanque.idtanquefisico));

function IOForm({ data, tipoIO, sending, close, onForm }) {
  const formRef = useRef(null);
  const tanquesComboboxLoadedRef = useRef(false);
  const alarmesTanquesLoadedRef = useRef({});

  const initialData = useMemo(() => {
    const tanquesEntrada = normalizaTanquesEntrada(data?.tanques || []);

    return {
      ...data,
      tanquesEntrada,
      tanques: tanquesEntrada
        .map((tanque) => tanque.idtanquefisico)
        .filter((value) => value !== null && value !== undefined),

      alarmes: (data?.alarmes || [])
        .map((alarme) =>
          typeof alarme === 'object' ? alarme.idconfigalarmetanque : alarme
        )
        .filter((value) => value !== null && value !== undefined),
    };
  }, [data]);

  const [tanquesEntrada, setTanquesEntrada] = useState(
    initialData.tanquesEntrada || []
  );

  const [alarmesSelecionados, setAlarmesSelecionados] = useState(
    initialData.alarmes || []
  );

  const alarmesIniciaisPorTanque = useMemo(
    () =>
      new Map(
        (initialData.tanquesEntrada || []).map((tanque) => [
          Number(tanque.idtanquefisico),
          (tanque.alarmes || []).map(
            (alarme) => alarme.idconfigalarmetanque
          ),
        ])
      ),
    [initialData.tanquesEntrada]
  );

  const modoSegurancaOptions = useMemo(
    () => [
      { value: 0, label: 'Normalmente aberto' },
      { value: 1, label: 'Normalmente fechado' },
    ],
    []
  );

  const modoOperacaoOptions = useMemo(
    () => [
      { value: 0, label: 'Contínuo' },
      { value: 1, label: 'Intermitente' },
    ],
    []
  );

  const tipoDesativacaoOptions = useMemo(
    () => [
      { value: 0, label: 'No reconhecimento' },
      { value: 1, label: 'Na normalização' },
      { value: 2, label: 'No reconhecimento e na normalização' },
    ],
    []
  );

  useEffect(() => {
    setTanquesEntrada(initialData.tanquesEntrada || []);
    setAlarmesSelecionados(initialData.alarmes || []);
    tanquesComboboxLoadedRef.current = false;
    alarmesTanquesLoadedRef.current = {};
  }, [initialData]);

  const handleChangeTanques = useCallback(
    (value, opcoesSelecionadas = []) => {
      const idsTanques = (value || [])
        .map(getIdTanque)
        .filter((idtanquefisico) => !Number.isNaN(idtanquefisico));
      const tanquesSelecionados = new Map(
        (opcoesSelecionadas || []).map((tanque) => [
          Number(tanque.idtanquefisico),
          tanque,
        ])
      );

      if (
        isEmpty(idsTanques) &&
        !tanquesComboboxLoadedRef.current &&
        hasItems(initialData.tanques)
      ) {
        tanquesComboboxLoadedRef.current = true;
        return;
      }

      tanquesComboboxLoadedRef.current = true;

      setTanquesEntrada((state) =>
        idsTanques.map((idtanquefisico) => {
          const tanqueAtual =
            state.find(
              (tanque) =>
                Number(tanque.idtanquefisico) === Number(idtanquefisico)
            ) ||
            initialData.tanquesEntrada.find(
              (tanque) =>
                Number(tanque.idtanquefisico) === Number(idtanquefisico)
            ) ||
            tanquesSelecionados.get(Number(idtanquefisico));

          return {
            ...(tanqueAtual || {}),
            idtanquefisico,
            alarmes: tanqueAtual?.alarmes || [],
          };
        })
      );

      formRef.current?.setFieldValue('tanques', idsTanques);
    },
    [initialData.tanquesEntrada]
  );

  const handleChangeAlarmesTanque = useCallback(
    (idtanquefisico, alarmes, opcoesSelecionadas = []) => {
      const alarmesNormalizados = normalizaAlarmes(
        isEmpty(opcoesSelecionadas) ? alarmes || [] : opcoesSelecionadas
      );

      setTanquesEntrada((state) => {
        const tanqueAtual = state.find(
          (tanque) => Number(tanque.idtanquefisico) === Number(idtanquefisico)
        );

        if (
          isEmpty(alarmesNormalizados) &&
          !alarmesTanquesLoadedRef.current[idtanquefisico] &&
          hasItems(tanqueAtual?.alarmes)
        ) {
          alarmesTanquesLoadedRef.current[idtanquefisico] = true;
          return state;
        }

        alarmesTanquesLoadedRef.current[idtanquefisico] = true;

        return state.map((tanque) => {
          if (Number(tanque.idtanquefisico) !== Number(idtanquefisico)) {
            return tanque;
          }

          return {
            ...tanque,
            alarmes: alarmesNormalizados,
          };
        });
      });
    },
    []
  );

  const handleSubmit = useCallback(
    async (values) => {
      try {
        const tanques = tanquesEntrada || [];
        const alarmes = values.alarmes || alarmesSelecionados || [];
        const entradaConfigurada =
          isTextFilled(values.nomeconversorio) || hasItems(tanques);
        const saidaConfigurada =
          isTextFilled(values.nomeconversorio) ||
          isFilled(values.modosegurancaconversorio) ||
          isFilled(values.modooperacaoconversorio) ||
          isFilled(values.tipodesativacaoconversorio) ||
          hasItems(alarmes);

        const schema =
          tipoIO === 'E'
            ? Yup.object().shape({
                nomeconversorio: entradaConfigurada
                  ? Yup.string()
                      .trim()
                      .required(t('s.geral.validacao.campoobrigatorio'))
                  : Yup.string().nullable(),
                tanques: entradaConfigurada
                  ? Yup.array()
                      .min(1, t('s.geral.validacao.campoobrigatorio'))
                      .required(t('s.geral.validacao.campoobrigatorio'))
                  : Yup.array().nullable(),
              })
            : Yup.object().shape({
                nomeconversorio: saidaConfigurada
                  ? Yup.string()
                      .trim()
                      .required(t('s.geral.validacao.campoobrigatorio'))
                  : Yup.string().nullable(),
                modosegurancaconversorio: saidaConfigurada
                  ? Yup.number()
                      .required(t('s.geral.validacao.campoobrigatorio'))
                      .typeError(t('s.geral.validacao.campoobrigatorio'))
                  : Yup.mixed().nullable(),
                modooperacaoconversorio: saidaConfigurada
                  ? Yup.number()
                      .required(t('s.geral.validacao.campoobrigatorio'))
                      .typeError(t('s.geral.validacao.campoobrigatorio'))
                  : Yup.mixed().nullable(),
                tipodesativacaoconversorio: saidaConfigurada
                  ? Yup.number()
                      .required(t('s.geral.validacao.campoobrigatorio'))
                      .typeError(t('s.geral.validacao.campoobrigatorio'))
                  : Yup.mixed().nullable(),
                alarmes: saidaConfigurada
                  ? Yup.array()
                      .min(1, t('s.geral.validacao.campoobrigatorio'))
                      .required(t('s.geral.validacao.campoobrigatorio'))
                  : Yup.array().nullable(),
              });

        await validation(schema, {
          ...values,
          tanques,
          alarmes,
        });

        const formData = {
          ...data,
          portaconversorio: Number(data.portaconversorio),
          tipoconversorio: tipoIO,
        };

        if (tipoIO === 'E') {
          formData.nomeconversorio = entradaConfigurada
            ? values.nomeconversorio
            : '';
          formData.tanques = entradaConfigurada
            ? tanques.map((tanque) => ({
                ...tanque,
                idtanquefisico: getIdTanque(tanque),
                alarmes: normalizaAlarmes(tanque.alarmes || []).map(
                  (alarme) => ({
                    idconfigalarmetanque: getIdConfigAlarmeTanque(alarme),
                  })
                ),
              }))
            : [];
          formData.alarmes = [];
        }

        if (tipoIO === 'S') {
          formData.nomeconversorio = saidaConfigurada
            ? values.nomeconversorio
            : '';
          formData.modosegurancaconversorio = saidaConfigurada
            ? Number(values.modosegurancaconversorio)
            : null;
          formData.modooperacaoconversorio = saidaConfigurada
            ? Number(values.modooperacaoconversorio)
            : null;
          formData.tipodesativacaoconversorio = saidaConfigurada
            ? Number(values.tipodesativacaoconversorio)
            : null;

          formData.alarmes = saidaConfigurada
            ? alarmes.map((idconfigalarmetanque) => ({
                idconfigalarmetanque: Number(idconfigalarmetanque),
              }))
            : [];
        }

        onForm(formData);
        close();
      } catch (error) {
        validationErrors(error, formRef);
      }
    },
    [data, tipoIO, close, onForm, tanquesEntrada, alarmesSelecionados]
  );

  useEffect(() => {
    if (sending) formRef.current.submitForm();
  }, [sending]);

  return (
    <S.Wrapper ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
      <Hidden name="id" />
      <Hidden name="tipoconversorio" />

      <Input name="portaconversorio" label="IO" type="number" disabled />

      {tipoIO === 'E' && (
        <>
          <ComboboxMultiple
            name="tanques"
            label="codigotanquefisico"
            url="tanquesfisicos"
            unique="idtanquefisico"
            title="Tanques"
            defaultValue={initialData.tanques}
            disabled={!data?.idposto}
            params={{
              wherefield: ['idposto'],
              valuewherefield: [data?.idposto],
              typewherefield: ['EQUALS'],
              pesquisalista: true,
            }}
            getValue={handleChangeTanques}
          />

          {hasItems(tanquesEntrada) && (
            <S.TanquesAlarmes>
              {tanquesEntrada.map((tanque) => (
                <S.TanqueAlarme key={tanque.idtanquefisico}>
                  <S.TanqueHeader>
                    <strong>
                      {tanque.codigotanquefisico ||
                        tanque.nomeconversorio ||
                        tanque.nometanquefisico ||
                        `Tanque ${tanque.idtanquefisico}`}
                    </strong>
                  </S.TanqueHeader>

                  <ComboboxMultiple
                    name={`alarmes_tanque_${tanque.idtanquefisico}`}
                    label="nomeconfigalarmetanquecombo"
                    url="configuracaoalarmetanques"
                    unique="idconfigalarmetanque"
                    title="Alarmes"
                    ehTraduzido
                    defaultValue={
                      alarmesIniciaisPorTanque.get(
                        Number(tanque.idtanquefisico)
                      ) || EMPTY_ARRAY
                    }
                    getValue={(value, opcoesSelecionadas) =>
                      handleChangeAlarmesTanque(
                        tanque.idtanquefisico,
                        value || [],
                        opcoesSelecionadas || []
                      )
                    }
                  />
                </S.TanqueAlarme>
              ))}
            </S.TanquesAlarmes>
          )}
        </>
      )}

      {tipoIO === 'S' && (
        <>
          <Select
            name="modosegurancaconversorio"
            label={t('s.configuracao.conversor.modosegurancaconversorio')}
            options={modoSegurancaOptions}
          />

          <Select
            name="modooperacaoconversorio"
            label={t('s.configuracao.conversor.modooperacaoconversorio')}
            options={modoOperacaoOptions}
          />

          <Select
            name="tipodesativacaoconversorio"
            label={t('s.configuracao.conversor.tipodesativacaoconversorio')}
            options={tipoDesativacaoOptions}
          />

          <ComboboxMultiple
            name="alarmes"
            label="nomeconfigalarmetanquecombo"
            url="configuracaoalarmetanques"
            unique="idconfigalarmetanque"
            title="Alarmes"
            ehTraduzido
            defaultValue={initialData.alarmes}
            getValue={(value) => {
              setAlarmesSelecionados(value || []);
              formRef.current?.setFieldValue('alarmes', value || []);
            }}
          />
        </>
      )}

      <Input name="nomeconversorio" label="Nome" />
    </S.Wrapper>
  );
}

IOForm.propTypes = {
  data: PropTypes.shape({
    alarmes: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.shape({}),
      ])
    ),
    idposto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    portaconversorio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tanques: PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
        PropTypes.shape({}),
      ])
    ),
  }),
  tipoIO: PropTypes.string.isRequired,
  close: PropTypes.func.isRequired,
  onForm: PropTypes.func.isRequired,
  sending: PropTypes.bool.isRequired,
};

IOForm.defaultProps = {
  data: {},
};

export default memo(IOForm);
